import { DESIGN_ASSET_TYPES, type CadFile } from '@cutr/constants/order';
import { useRouter, useTrack } from '@/composables/plugins';
import { useOrderStore } from '@/stores/order';
import { useOrderFiles } from '@/stores/orderFiles';
import {
  useActiveFileSelection,
  useBuildOrderTabLocation,
} from '@/components/elements/Order/composables';
import { useOrderNavigation } from './useOrderNavigation';

/**
 * What a new file earns beyond its own cache write: tracking, selection, and the
 * cabinet detour.
 *
 * The invalidation is awaited first: selection refuses an id the cached list does
 * not hold, and anything that selects the new row as it lands would put a second
 * route push in flight — the two cancel, leaving the URL on the file we came from.
 */
export function useFileUploadEffects() {
  const { selectActiveFile } = useActiveFileSelection();
  const orderStore = useOrderStore();
  const filesStore = useOrderFiles();
  const navigation = useOrderNavigation();
  const buildOrderTabLocation = useBuildOrderTabLocation();
  const router = useRouter();
  const $track = useTrack();

  async function onFileUploaded(newFile: CadFile) {
    await filesStore.invalidateFiles();

    // The cabinator already shows the design asset it just wrote.
    if (
      DESIGN_ASSET_TYPES.includes(newFile.assetType) &&
      navigation.currentStep.value === 'cabinator'
    )
      return;

    $track('order:cadfile-upload', {
      order: orderStore.currentOrder,
      filetype: newFile.filetype,
      source: newFile.assetType,
    });

    // A cabinet opens in the editor. Step and file travel in one navigation:
    // selecting and then changing step puts two pushes in flight, and the
    // second commits the query the first had not written yet.
    if (
      newFile.type === 'cabinet' &&
      navigation.currentStep.value !== 'cabinator'
    )
      return router.push(
        buildOrderTabLocation('cabinator', { query: { fileId: newFile.id } })
      );

    await selectActiveFile(newFile.id);
  }

  return { onFileUploaded };
}
